<script setup lang="ts">
/**
 * FE-034 — cookie consent banner (Figma `Cookies` 39:41637: desktop 39:41638, mobile 39:41647).
 * Shows on the client until a choice is stored in localStorage (`fair.cookieConsent`).
 */
import { onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { FairButton } from '@/ui/primitives/FairButton'
import { PRIVACY_POLICY_ROUTE } from '@/router/routes'
import { useFairCookieConsent } from '../store/useFairCookieConsentStore'
import {
  FAIR_COOKIE_BANNER_BOTTOM_OFFSET_PX,
  FAIR_COOKIE_BANNER_DESKTOP_WIDTH_PX,
  FAIR_COOKIE_BANNER_SIDE_MARGIN_PX,
} from '../fair-cookie-consent.data'
import './fair-cookie-banner.css'

const COOKIE_IMAGE = '/fair-cookie/cookie.png'

const { t } = useI18n()
const consent = useFairCookieConsent()
const { bannerVisible } = storeToRefs(consent)

onMounted(() => consent.hydrate())

const hostStyle = {
  '--fair-cookie-banner-bottom': `${FAIR_COOKIE_BANNER_BOTTOM_OFFSET_PX}px`,
  '--fair-cookie-banner-width': `${FAIR_COOKIE_BANNER_DESKTOP_WIDTH_PX}px`,
  '--fair-cookie-banner-side-margin': `${FAIR_COOKIE_BANNER_SIDE_MARGIN_PX}px`,
}
</script>

<template>
  <Teleport to="body">
    <Transition name="fair-cookie-banner">
      <section
        v-if="bannerVisible"
        class="fair-cookie-banner"
        :style="hostStyle"
        role="dialog"
        aria-modal="false"
        :aria-label="t('fair_cookie_banner.aria_label')"
        data-testid="fair-cookie-banner"
      >
        <div class="fair-cookie-banner__content">
          <img
            class="fair-cookie-banner__image"
            :src="COOKIE_IMAGE"
            alt=""
            aria-hidden="true"
          >
          <p class="fair-cookie-banner__text">
            <i18n-t
              keypath="fair_cookie_banner.text"
              tag="span"
              scope="global"
            >
              <template #policy>
                <RouterLink
                  class="fair-cookie-banner__policy"
                  :to="PRIVACY_POLICY_ROUTE"
                >
                  {{ t('fair_cookie_banner.policy') }}
                </RouterLink>
              </template>
            </i18n-t>
          </p>
        </div>
        <div class="fair-cookie-banner__footer">
          <FairButton
            class="fair-cookie-banner__button fair-cookie-banner__button--reject"
            variant="tonal"
            size="m"
            :rounded="false"
            :label="t('fair_cookie_banner.reject')"
            data-testid="fair-cookie-reject"
            @click="consent.rejectNonEssential()"
          />
          <FairButton
            class="fair-cookie-banner__button"
            variant="primary"
            size="m"
            :rounded="false"
            :label="t('fair_cookie_banner.accept')"
            data-testid="fair-cookie-accept"
            @click="consent.acceptAll()"
          />
        </div>
      </section>
    </Transition>
  </Teleport>
</template>
